// Centralized data cache to reduce redundant database calls
import { supabase } from './supabase';

class DataCache {
  constructor() {
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  // Generic cache getter with TTL
  get(key) {
    const timestamp = this.cacheTimestamps.get(key);
    if (timestamp && Date.now() - timestamp < this.CACHE_DURATION) {
      return this.cache.get(key);
    }
    return null;
  }

  // Generic cache setter
  set(key, value) {
    this.cache.set(key, value);
    this.cacheTimestamps.set(key, Date.now());
  }

  // Clear expired entries
  clearExpired() {
    const now = Date.now();
    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      if (now - timestamp >= this.CACHE_DURATION) {
        this.cache.delete(key);
        this.cacheTimestamps.delete(key);
      }
    }
  }

  // Cached upvote counts fetch
  async getUpvoteCounts() {
    const cacheKey = 'upvote_counts';
    let data = this.get(cacheKey);
    
    if (!data) {
      try {
        const { data: upvoteData, error } = await supabase.rpc('fetch_upvote_counts');
        if (error) throw error;
        data = upvoteData;
        this.set(cacheKey, data);
      } catch (error) {
        console.error('Error fetching upvote counts:', error);
        return [];
      }
    }
    
    return data;
  }

  // Cached golf courses fetch with filters
  async getGolfCourses(filters = {}) {
    const cacheKey = `golf_courses_${JSON.stringify(filters)}`;
    let data = this.get(cacheKey);
    
    if (!data) {
      try {
        let query = supabase.from('golf_courses').select('*');
        
        // Apply filters
        if (filters.course_country) {
          query = query.eq('course_country', filters.course_country);
        }
        if (filters.course_county) {
          query = query.eq('course_county', filters.course_county);
        }
        if (filters.orderBy) {
          query = query.order(filters.orderBy.field, { ascending: filters.orderBy.ascending });
        }
        if (filters.limit) {
          query = query.limit(filters.limit);
        }
        
        const { data: coursesData, error } = await query;
        if (error) throw error;
        
        data = coursesData;
        this.set(cacheKey, data);
      } catch (error) {
        console.error('Error fetching golf courses:', error);
        return [];
      }
    }
    
    return data;
  }

  // Cached architect rankings
  async getArchitectRankings(filters = {}) {
    const cacheKey = `architect_rankings_${JSON.stringify(filters)}`;
    let data = this.get(cacheKey);
    if (!data) {
      const { fetchArchitectRankings } = await import('./fetchArchitects');
      data = await fetchArchitectRankings(filters);
      this.set(cacheKey, data);
    }
    return data;
  }

  // Cached architect by slug
  async getArchitectBySlug(slug) {
    const cacheKey = `architect_${slug}`;
    let data = this.get(cacheKey);
    if (!data) {
      const { fetchArchitectBySlug } = await import('./fetchArchitects');
      data = await fetchArchitectBySlug(slug);
      this.set(cacheKey, data);
    }
    return data;
  }

  // Clear specific cache entry
  clear(key) {
    this.cache.delete(key);
    this.cacheTimestamps.delete(key);
  }

  // Clear all cache
  clearAll() {
    this.cache.clear();
    this.cacheTimestamps.clear();
  }
}

// Export singleton instance
export const dataCache = new DataCache();

// Auto-cleanup expired entries every minute
setInterval(() => {
  dataCache.clearExpired();
}, 60000);
